import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextareaAutosize
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { apiServices } from '../../../services/apiServices';
import Swal from 'sweetalert2';

const RedemptionManagement = () => {
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchRedemptionRequests = async (pageNum = 0, limit = 25) => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        page: pageNum + 1,
        limit: limit
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await apiServices.getAPI(`admin/redemption-requests?${params.toString()}`);

      if (response.data.success) {
        setRedemptionRequests(response.data.data.requests);
        setTotalCount(response.data.data.pagination.total_count);
      } else {
        setError('Failed to fetch redemption requests');
      }
    } catch (err) {
      console.error('Error fetching redemption requests:', err);
      setError('Error fetching redemption requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchRedemptionRequests(0, rowsPerPage);
  };

  // Convert redemption requests data to tabular format (for CSV)
  const convertRequestsToTabularFormat = (requests) => {
    if (!requests || requests.length === 0) {
      return [];
    }

    // Define headers
    const headers = [
      'Request ID',
      'IAPM ID',
      'Name',
      'Email',
      'Phone',
      'Amount',
      'Status',
      'Request Date',
      'Admin Notes',
      'Payment Reference',
      'Updated Date'
    ];

    // Convert requests to data rows
    const dataRows = requests.map(request => [
      request.request_id || '',
      request.peacekeeper_id || '',
      request.username || '',
      request.email || '',
      request.mobile || '',
      request.reward_amount ? `$${parseFloat(request.reward_amount).toFixed(2)}` : '',
      request.status || '',
      request.request_date ? new Date(request.request_date).toLocaleDateString() : '',
      request.admin_notes || '',
      request.payment_reference || '',
      request.updated_at ? new Date(request.updated_at).toLocaleDateString() : ''
    ]);

    // Combine headers and rows
    return [headers, ...dataRows];
  };

  // Download CSV file
  const downloadCSV = (requests, filename) => {
    try {
      // Convert requests data to tabular format
      const csvData = convertRequestsToTabularFormat(requests);
      
      // Convert 2D array to CSV string
      const csvContent = csvData.map(row => 
        row.map(cell => {
          // Escape double quotes by doubling them
          const cellStr = String(cell).replace(/"/g, '""');
          // Wrap cell in quotes if it contains comma, newline, or double quote
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr}"`;
          }
          return cellStr;
        }).join(',')
      ).join('\n');
      
      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create a temporary link element and trigger download
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      console.log(`CSV file ${filename} downloaded successfully`);
    } catch (error) {
      console.error('Error creating CSV file:', error);
      setError('Error creating CSV file. Please try again.');
    }
  };

  // Handle export redemption requests
  const handleExportRequests = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Starting export of all redemption requests...');
      
      // First, get the total count to know how many pages we need
      const initialParams = new URLSearchParams({
        page: 1,
        limit: 100 // API maximum limit
      });

      // Add current filters if any
      if (searchTerm.trim()) {
        initialParams.append('search', searchTerm.trim());
      }
      if (statusFilter) {
        initialParams.append('status', statusFilter);
      }

      const initialResponse = await apiServices.getAPI(`admin/redemption-requests?${initialParams.toString()}`);
      
      if (!initialResponse.data.success) {
        setError('Failed to fetch redemption requests for export');
        return;
      }

      const totalRequests = initialResponse.data.data.pagination.total_count;
      const totalPages = Math.ceil(totalRequests / 100);
      
      console.log(`Total requests to export: ${totalRequests}, Total pages: ${totalPages}`);
      
      let allRequests = [...initialResponse.data.data.requests];
      
      // Fetch remaining pages if there are more than 1 page
      if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page++) {
          const pageParams = new URLSearchParams({
            page: page,
            limit: 100
          });

          // Add current filters if any
          if (searchTerm.trim()) {
            pageParams.append('search', searchTerm.trim());
          }
          if (statusFilter) {
            pageParams.append('status', statusFilter);
          }

          const pageResponse = await apiServices.getAPI(`admin/redemption-requests?${pageParams.toString()}`);
          
          if (pageResponse.data.success && pageResponse.data.data.requests) {
            allRequests = [...allRequests, ...pageResponse.data.data.requests];
            console.log(`Fetched page ${page}/${totalPages}, Total requests so far: ${allRequests.length}`);
          } else {
            console.warn(`Failed to fetch page ${page}`);
          }
        }
      }
      
      console.log(`Exporting ${allRequests.length} requests...`);
      
      // Download CSV file
      downloadCSV(allRequests, 'redemption_requests_export.csv');
      
      console.log('Export completed successfully');
    } catch (err) {
      console.error('Error exporting redemption requests:', err);
      setError('Error exporting redemption requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpdateModal = (request) => {
    setSelectedRequest(request);
    setUpdateStatus(request.status);
    setAdminNotes(request.admin_notes || '');
    setPaymentReference(request.payment_reference || '');
    setUpdateModalOpen(true);
  };

  const handleCloseUpdateModal = () => {
    setUpdateModalOpen(false);
    setSelectedRequest(null);
    setUpdateStatus('');
    setAdminNotes('');
    setPaymentReference('');
  };

  const handleUpdateStatus = async () => {
    if (!selectedRequest || !updateStatus) {
      Swal.fire('Error', 'Please select a status', 'error');
      return;
    }

    setUpdating(true);
    try {
      const adminDataStr = localStorage.getItem('adminData');
      const adminData = adminDataStr ? JSON.parse(adminDataStr) : null;

      const response = await apiServices.putAPI(`admin/redemption-requests/${selectedRequest.request_id}/status`, {
        status: updateStatus,
        adminNotes: adminNotes,
        paymentReference: paymentReference,
        adminUserId: adminData?.userid || 1
      });

      if (response.data) {
        Swal.fire({
          title: 'Success',
          text: 'Redemption request status updated successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        });

        handleCloseUpdateModal();
        fetchRedemptionRequests(page, rowsPerPage);
      }
    } catch (err) {
      console.error('Error updating request status:', err);
      Swal.fire('Error', 'Failed to update request status', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(0);
    fetchRedemptionRequests();
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'warning';
      case 'APPROVED': return 'info';
      case 'REJECTED': return 'error';
      case 'COMPLETED': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  useEffect(() => {
    fetchRedemptionRequests(page, rowsPerPage);
  }, [page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        fetchRedemptionRequests(0, rowsPerPage);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, rowsPerPage]);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', pr: 8, mr: 4 }}>
      {/* Search and Filters */}
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef'}}>
        <CardContent sx={{ py: 2, px: 3 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by username, PeaceKeeper ID, or email..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6c757d' }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#214F7D',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#214F7D',
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl size="small" sx={{ minWidth: '120px' }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{
                    backgroundColor: '#fff',
                    '& .MuiSelect-select': { paddingRight: '32px' }
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={handleApplyFilters}
                  size="small"
                  sx={{
                    backgroundColor: '#214F7D',
                    color: '#fff',
                    '&:hover': { backgroundColor: '#1a3f63' },
                    px: 2, py: 1, borderRadius: '6px',
                    textTransform: 'none', fontWeight: 500, minWidth: '80px',
                  }}
                >
                  Apply
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportRequests}
                  size="small"
                  disabled={redemptionRequests.length === 0}
                  sx={{
                    borderColor: '#28a745', color: '#28a745',
                    '&:hover': { borderColor: '#1e7e34', backgroundColor: 'rgba(40, 167, 69, 0.04)' },
                    px: 2, py: 1, borderRadius: '6px',
                    textTransform: 'none', fontWeight: 500, minWidth: '80px',
                  }}
                >
                  Export
                </Button>
                <Tooltip title="Refresh">
                  <IconButton onClick={handleRefresh} size="small"
                    sx={{
                      border: '1px solid #dee2e6', backgroundColor: '#fff', width: '40px',
                      height: '40px',
                      '&:hover': { backgroundColor: '#f8f9fa', borderColor: '#214F7D' },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: '1.1rem', color: '#6c757d' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Redemption Requests Table */}
      <Paper sx={{ width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'auto' }}>
        <TableContainer sx={{ maxHeight: 500, overflowX: 'auto', overflowY: 'auto' }}>
          <Table stickyHeader sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Request ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>IAPM ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 150 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 120, whiteSpace: 'nowrap' }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#214F7D', py: 1, fontSize: '0.875rem', minWidth: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Loading redemption requests...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : redemptionRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No redemption requests found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                redemptionRequests.map((request) => (
                  <TableRow key={request.request_id} hover sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight="bold" color="primary" sx={{ fontSize: '0.875rem' }}>
                        #{request.request_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {request.peacekeeper_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {request.username}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#6c757d' }}>
                        {request.email}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {request.mobile}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#28a745' }}>
                        {formatCurrency(request.reward_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          '& .MuiChip-label': { fontSize: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatDate(request.request_date)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3 }}>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenUpdateModal(request)}
                          sx={{
                            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      </Paper>

      {/* Summary */}
      <Box sx={{
        mt: 2, p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          <strong>Total Requests:</strong> {totalCount} | <strong>Showing:</strong> {redemptionRequests.length} of {totalCount}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
        </Typography>
      </Box>

      {/* Update Status Modal */}
      <Dialog
        open={updateModalOpen}
        onClose={handleCloseUpdateModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{
          pb: 3,
          pt: 4,
          px: 4,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#214F7D' }}>
            Update Redemption Request Status
          </Typography>
          {selectedRequest && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`Request #${selectedRequest.request_id}`}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Typography variant="body2" color="text.secondary">
                {selectedRequest.username} - {formatCurrency(selectedRequest.reward_amount)}
              </Typography>
            </Box>
          )}
        </DialogTitle>

        <DialogContent sx={{ pb: 2, pt: '32px !important', px: 4 }}>
          <Grid container spacing={3}>
            {/* Status Selection */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontWeight: 500 }}>Status</InputLabel>
                <Select
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                  label="Status"
                  sx={{
                    '& .MuiSelect-select': {
                      fontWeight: 500
                    }
                  }}
                >
                  <MenuItem value="PENDING">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#ff9800'
                      }} />
                      Pending
                    </Box>
                  </MenuItem>
                  <MenuItem value="APPROVED">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#2196f3'
                      }} />
                      Approved
                    </Box>
                  </MenuItem>
                  <MenuItem value="REJECTED">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#f44336'
                      }} />
                      Rejected
                    </Box>
                  </MenuItem>
                  <MenuItem value="COMPLETED">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4caf50'
                      }} />
                      Completed
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Payment Reference */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Payment Reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                size="medium"
                placeholder="Enter payment transaction reference"
                helperText="Optional: Add payment transaction ID or reference"
              />
            </Grid>

            {/* Admin Notes */}
            <Grid item xs={12}>
              {/* <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                 Admin Notes
               </Typography> */}
              <TextField
                fullWidth
                multiline
                rows={4}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Enter admin notes or comments about this request..."
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fafafa'
                  }
                }}
              />
            </Grid>

            {/* Current Request Details */}
            {selectedRequest && (
              <Grid size={{ xs: 12 }}>
                <Card sx={{ p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                  <Typography variant="500" sx={{ mb: 1, fontWeight: 600, color: '#333' }}>
                    Request Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">IAPM ID</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedRequest.peacekeeper_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Amount</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#28a745' }}>
                        {formatCurrency(selectedRequest.reward_amount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary">Request Date</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedRequest.request_date)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3} className="d-flex flex-direction-column">
                      <Typography variant="caption" color="text.secondary">Current Status</Typography>
                      <Chip
                        label={selectedRequest.status}
                        color={getStatusColor(selectedRequest.status)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          px: 4,
          py: 3,
          pt: 3,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}>
          <Button
            onClick={handleCloseUpdateModal}
            disabled={updating}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: '#ccc',
              color: '#666'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            variant="contained"
            disabled={updating || !updateStatus}
            startIcon={updating ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={{
              minWidth: 140,
              backgroundColor: '#214F7D',
              '&:hover': {
                backgroundColor: '#1a3f63'
              }
            }}
          >
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RedemptionManagement;
